

import { IDataAccessor, AbstractAccessorService } from "@core/data-access";
import { IResponse } from "@core/data-access/model";
import { IProductInstance } from "@modules/product-instance/model/domain/ProductInstance";
import { AttributeValue } from "@modules/product/model/domain/attribute/AttributeValue";
import { ProductData } from "@modules/product/model/domain/data/ProductData";
import { IProductPrice } from "@modules/product/model/domain/pricing/product-price";
import { Matrix } from "@modules/product/pricing/model/domain/Matrix";
import { IProductData } from "@modules/product/product/model/domain/IProductData";
import { IProductPricingRestService } from "./product-pricing-rest";
import { IProjectName } from "@modules/product-instance/model/domain/project-name";
import { isUndefined } from "@core/comparators/public-api";
import { ICraftAttributeQty } from "@modules/product/model/dto/CraftAttributeQty";
import { IProductCraftPrice } from "@modules/product/model/dto/ProductCraftPrice";
import { ParameterType } from "@core/data-access/abstract-accessor.service";
import is from "@core/comparators/is";
import { IPriceList } from "@modules/product/model/dto/price/productPriceList";
import { IDiscretePriceList } from "@modules/product/pricing/model/domain/DiscreteList";
import { IPriceFeatureInfo } from "@modules/product/pricing/model/domain/PriceFeatureInfo";


export class ProductPricingRestService extends AbstractAccessorService<IProductPrice> implements IProductPricingRestService {
    // 离散数量价格表价格缓存
    private discreteCache: Map<string, any> = new Map();

    public readonly $name: string = "productPricingRestService";

    public static readonly $inject: string[] = ["$accessor", "$q"];

    constructor(public $accessor: IDataAccessor<IProductPrice>, public $q: angular.IQService) {
        super();
    }

    /**
     * @description 简易价格计算
     * @param configurableProductId 可配置产品ID
     * @returns 
     */
    public list(productId: number): angular.IPromise<IResponse<Array<IProductPrice>>> {
        return this.$accessor<Array<IProductPrice>, { productId: number }, string>(this.$name, "list", {
            parameter: {
                productId: productId
            }
        });
    }

    /**
     * @description post简易价格计算
     * @param productId 产品ID
     * @param productAttr
     * @param currencyCode
     * @param region 地区
     * @param versionedAttributeId 属性版本id
     * @param designMethod 设计模式
     * @returns 
     */
    public listPost(productId: number, productAttr: { attributeValues: Array<AttributeValue> }, currencyCode: string, region: string | undefined, versionedAttributeId: number | undefined, designMethod: string | undefined): angular.IPromise<IResponse<IProductPrice[]>> {
        let _requestConfig = {
            parameter: {
                productId: productId
            },
            data: {
                product: productAttr,
                productId: productId,
                qty: 0,
                currencyCode: currencyCode
            }
        }
        if (!angular.isUndefined(versionedAttributeId)) {
            _requestConfig.parameter['versionedAttributeId'] = versionedAttributeId;
        }
        if (!angular.isUndefined(designMethod)) {
            _requestConfig.data['designMethod'] = designMethod;
        }
        if (region) {
            _requestConfig['headers'] = {
                "saleRegions-country": region
            }
        }

        return this.$accessor<IProductPrice[], any, string>(this.$name, "listPost", _requestConfig)
    }
    /**
     * @description list post特定价格计算（地区）
     * @param productId 产品ID
     * @param sourceDataDTO
     * @param region
     * @returns 
     */
    public regionListPost(productId: number, productData: any, region: string): angular.IPromise<IResponse<Array<IProductPrice>>> {
        let _requestConfig = {
            parameter: {
                productId: productId
            },
            data: {
                productId: productId,
                product: productData,
                qty: 0
            },
            headers: {
                "saleRegions-country": region
            }
        }
        return this.$accessor<Array<IProductPrice>, any, string>(this.$name, "listPost", _requestConfig);
    }
    /**
    * @description list post特定价格计算（us）
    * @param productId 产品ID
    * @param sourceDataDTO
    * @returns 
    */

    public usRegionListPost(productId: number, sourceDataDTO: any): angular.IPromise<IResponse<Array<IProductPrice>>> {
        return this.regionListPost(productId, sourceDataDTO, "US");
    };
    /**
     * @description 复杂价格计算(通过产品属性)
     * @param configurableProductId 可配置产品ID
     * @param product 
     * @param quantities 价格区间
     * @returns 
     */
    public matrix(productId: number, product, quantities: Array<number>): angular.IPromise<IResponse<Matrix>> {
        return this.$accessor<Matrix, { productId: number, product, quantities: Array<number> }, string>(this.$name, "matrix", {
            parameter: {
                productId: productId,
                product: product,
                quantities: quantities
            }
        });
    }
    /**
     * @description 特定价格计算（地区）
     * @param productId 可配置产品ID
     * @returns 
     */
    public specificList(productId: number): angular.IPromise<IResponse<Array<IProductPrice>>> {
        return this.$accessor<Array<IProductPrice>, { productId: number }, string>(this.$name, "specificList", {
            parameter: {
                productId: productId
            }
        });
    }
    /**
     * @description 特定价格计算（地区）
     * @param productId 可配置产品ID
     * @returns 
     */
    public regionList(productId: number, region: string): angular.IPromise<IResponse<Array<IProductPrice>>> {
        let _requestConfig = {
            parameter: {
                productId: productId
            },
            headers: {
                "saleRegions-country": region
            }
        }
        return this.$accessor<Array<IProductPrice>, any, string>(this.$name, "list", _requestConfig);
    }
    /**
     * @description 特定价格计算（美国地区）
     * @param productId 可配置产品ID
     * @returns 
     */
    public usRegionList(configurableProductId: number): angular.IPromise<IResponse<Array<IProductPrice>>> {
        return this.regionList(configurableProductId, "US");
    }
    /**
     * @description 特定价格计算（地区）
     * @param configurableProductId 可配置产品ID
     * @param product 
     * @param quantities 价格区间
     * @param region 地区code
     */
    public regionMatrixList(productId: number, product, quantities: Array<number>, region: string): angular.IPromise<IResponse<Matrix>> {
        let _requestConfig = {
            parameter: {
                productId: productId
            },
            data: {
                productId: productId,
                product: product,
                quantities: quantities
            },
            headers: {
                "saleRegions-country": region
            }
        }
        return this.$accessor<Matrix, any, string>(this.$name, "matrix", _requestConfig);
    }
    /**
     * @description 复杂价格计算(通过产品属性)
     * @param configurableProductId 可配置产品ID
     * @param product 
     * @param quantities 价格区间
     */
    public specificMatrix(productId: number, product, quantities: Array<number>): angular.IPromise<IResponse<Matrix>> {
        let _requestConfig = {
            parameter: {
                productId: productId
            },
            data: {
                productId: productId,
                product: product,
                quantities: quantities
            }
        }
        return this.$accessor<Matrix, any, string>(this.$name, "specificMatrix", _requestConfig);
    }
    /**
     * @description: 获取productInstanceProjectName
     * @param {string} productId
     */
    public ProductInstanceProjectName(productId: string): angular.IPromise<IResponse<IProductInstance>> {
        return this.$accessor<IProductInstance, { productId: string }, string>(this.$name, "ProductInstanceProjectName", {
            parameter: {
                productId: productId
            }
        });
    }
    /**
     * @description: 获取ProjectName
     * @param {string} productInstanceId
     */
    public getProjectName(productInstanceId: string): angular.IPromise<IResponse<IProjectName>> {
        return this.$accessor<IProjectName, { productInstanceId: string }, string>(this.$name, "getProjectName", {
            parameter: {
                productInstanceId: productInstanceId
            }
        });
    }
    /**
     * @description 获取产品数据
     * @param productId 
     */
    public productData(productId: number): angular.IPromise<IResponse<IProductData>> {
        return this.$accessor<IProductData, { productId: number }, string>(this.$name, "productData", {
            parameter: {
                productId: productId
            }
        });
    }
    /**
     * @description get简易价格计算
     * @param productId 产品ID
     * @param currencyCode
     * @param versionedAttributeId 属性版本
     * @param region 地区
     * @param designMethod 设计模式
     * @returns 
     */
    public listGet(productId: number, currencyCode: string, versionedAttributeId: number | undefined, region: string | undefined, designMethod: string | undefined): angular.IPromise<IResponse<IProductPrice[]>> {
        let _requestConfig = {
            parameter: {
                productId: productId,
                currencyCode: currencyCode
            }
        }
        if (!angular.isUndefined(designMethod)) {
            _requestConfig.parameter['designMethod'] = designMethod;
        }
        if (!angular.isUndefined(versionedAttributeId)) {
            _requestConfig.parameter['versionedAttributeId'] = versionedAttributeId;
        }
        if (!angular.isUndefined(region)) {
            _requestConfig['headers'] = { "saleRegions-country": region }
        }

        return this.$accessor<IProductPrice[], any, string>(this.$name, "listGet", _requestConfig)
    }
    /**
     * @description 根据属性表单formId，get简易价格计算
     * @param productId 产品ID
     * @param formId 属性表单Id
     * @param currencyCode
     * @param versionedAttributeId 属性版本
     * @param region 地区
     * @param designMethod 设计模式
     * @returns 
     */
    public listGetByFormId(productId: number, formId: number, currencyCode: string, versionedAttributeId: number | undefined, region: string | undefined, designMethod: string | undefined): angular.IPromise<IResponse<IProductPrice[]>> {
        let _requestConfig = {
            parameter: {
                productId: productId,
                formId: formId,
                currencyCode: currencyCode
            }
        }
        if (!angular.isUndefined(designMethod)) {
            _requestConfig.parameter['designMethod'] = designMethod;
        }
        if (!angular.isUndefined(versionedAttributeId)) {
            _requestConfig.parameter['versionedAttributeId'] = versionedAttributeId;
        }
        if (!angular.isUndefined(region)) {
            _requestConfig['headers'] = { "saleRegions-country": region }
        }
        return this.$accessor<IProductPrice[], any, string>(this.$name, "listGet", _requestConfig)
    }

    /**
     * @description 新-获取网站whiteLabel产品数量价格表
     * @param productId 产品ID
     * @param currencyCode
     * @param productDesignMode
     * @param versionedAttributeId 属性版本
     * @param procuctInstanceId
     * @param propertyModelId
     * @returns 
     */
    public getListByStore(productId: number, currencyCode: string, productDesignMode: string | undefined, versionedAttributeId: number | undefined, procuctInstanceId: string | undefined, propertyModelId: string | undefined): angular.IPromise<IResponse<IPriceList>> {
        if (!is.undefined(versionedAttributeId)) {
            // 通过属性版本获取whiteLabelPrice
            let _requestConfig = {
                parameter: {
                    productId: productId,
                    versionedAttributeId: versionedAttributeId
                },
                headers: {
                    CurrencyCode: currencyCode
                }
            }
            // 设置产品设计模式参数
            if (!isUndefined(productDesignMode)) {
                _requestConfig.parameter['designMethod'] = productDesignMode;
            }
            // 设置ProductInstanceId参数
            if (!is.undefined(procuctInstanceId)) {
                _requestConfig.parameter['productInstanceId'] = procuctInstanceId;
            }
            // 设置propertyModelId参数
            if (!is.undefined(propertyModelId)) {
                _requestConfig.parameter['propertyModelId'] = propertyModelId;
            }

            return this.$accessor<IPriceList, {}, string>("productPricingRestService", "getListByStoreByVersionedAttr", _requestConfig);
        } else {
            // 通过属性版本获取whiteLabelPrice
            let _requestConfig = {
                parameter: {
                    productId: productId
                },
                headers: {
                    CurrencyCode: currencyCode
                }
            }
            // 设置产品设计模式参数
            if (!isUndefined(productDesignMode)) {
                _requestConfig.parameter['designMethod'] = productDesignMode;
            }
            // 设置ProductInstanceId参数
            if (!is.undefined(procuctInstanceId)) {
                _requestConfig.parameter['procuctInstanceId'] = procuctInstanceId;
            }
            // 设置propertyModelId参数
            if (!is.undefined(propertyModelId)) {
                _requestConfig.parameter['propertyModelId'] = propertyModelId;
            }
            return this.$accessor<IPriceList, {}, string>("productPricingRestService", "getListByStore", _requestConfig);
        }
    }

    /**
     * @description 新-获取网站whiteLabel产品单价
     * @param productId 产品ID
     * @param currencyCode
     * @param productDesignMode
     * @param versionedAttributeId 属性版本
     * @returns 
     */
    public getPriceByStore
        (productId: number, qty: number, currencyCode: string, productDesignMode: string | undefined, versionedAttributeId?: number): angular.IPromise<IResponse<IProductPrice>> {

        if (versionedAttributeId) {
            // 通过属性版本获取whiteLabelPrice
            let _requestConfig = {
                parameter: {
                    productId: productId,
                    qty: qty,
                    versionedAttributeId: versionedAttributeId
                },
                headers: {
                    CurrencyCode: currencyCode
                }
            }
            if (!isUndefined(productDesignMode)) {
                _requestConfig.parameter['designMethod'] = productDesignMode;
            }
            return this.$accessor<IProductPrice, {}, string>("productPricingRestService", "getPriceByStoreByVersionedAttr", _requestConfig);
        } else {
            // 通过属性版本获取whiteLabelPrice
            let _requestConfig = {
                parameter: {
                    productId: productId,
                    qty: qty
                },
                headers: {
                    CurrencyCode: currencyCode
                }
            }
            if (!isUndefined(productDesignMode)) {
                _requestConfig.parameter['designMethod'] = productDesignMode;
            }
            return this.$accessor<IProductPrice, {}, string>("productPricingRestService", "getPriceByStore", _requestConfig);
        }
    }
    /**
     * @description 随机卡产品计价（随机规则）
     * @param productInstanceId 
     * @param craftAttrQtys（随机工艺规则）
     */
    public getPriceByRandomRule(productInstanceId: number, craftAttrQtys: Array<ICraftAttributeQty>): angular.IPromise<IResponse<IProductCraftPrice>> {
        let _requestConfig = {
            parameter: {
                id: productInstanceId
            },
            data: {
                craftAttributeQtys: craftAttrQtys

            }
        }
        return this.$accessor<IProductCraftPrice, ParameterType, string>(this.$name, "getPriceByRandomRule", _requestConfig);
    }
    /**
    * @description 根据属性键值对获取网站whiteLabel产品-离散计价表
    * @param productId 
    * @param quantities
    * @param productAttributeValues
    * @param versionedAttributeId
    * 
    */
    getDiscretePriceByAttrs(productId: number, quantities: Array<number>, productAttributeValues: Array<AttributeValue>, versionedAttributeId: number | undefined): angular.IPromise<IResponse<IDiscretePriceList>> {
        let dataParam: {
            quantities: Array<number>;
            productAttributeValues: Array<AttributeValue>;
            
        } = {
            quantities: quantities,
            productAttributeValues: productAttributeValues,
        };
        let _requestConfig = {
            parameter: {
                productId: productId,
                versionedAttributeId: versionedAttributeId
            },
            data: dataParam
        }

        return this.$accessor<IDiscretePriceList, ParameterType, string>(this.$name, "getDiscretePriceByAttrs", _requestConfig);
    };

    /**
     * @description 通过店铺产品关联的whiteLabelProductId，获取离散计价表
     * @param productId
     * @param quantities
     * @param currencyCode
     * @param versionedAttributeId
     * @param propertyModelId
     */
    getDiscretePriceByStore(productId: number, quantities: Array<number>, currencyCode: string | undefined, versionedAttributeId: number | undefined, propertyModelId: string | undefined): angular.IPromise<IResponse<IDiscretePriceList>> {
        let dataParam: {
            quantities: Array<number>;
            propertyModelId?: string;
        } = {
            quantities: quantities,
        };
        if (!is.undefined(propertyModelId)) {
            dataParam.propertyModelId = propertyModelId;
        }
        let _requestConfig = {
            parameter: {
                productId: productId,
                versionedAttributeId: versionedAttributeId
            },
            data: dataParam,
            headers: {
                CurrencyCode: currencyCode,
            }
        }

        return this.$accessor<IDiscretePriceList, ParameterType, string>(this.$name, "getDiscretePriceByStore", _requestConfig);
    };

    /**
     * @description 获取指定产品的计价方式
     * @param productId
     * @param versionedAttributeId
     */
    getPriceFeatureInfo(productId: number, versionedAttributeId: number | undefined): angular.IPromise<IResponse<IPriceFeatureInfo>> {
        let _requestConfig = {
            parameter: {
                productId: productId,
                versionedAttributeId: versionedAttributeId
            }
        }
        return this.$accessor<IPriceFeatureInfo, ParameterType, string>(this.$name, "getPriceFeatureInfo", _requestConfig);
    };
}